import React, { useEffect, useState, memo } from 'react';
import { ClockIcon } from 'lucide-react';
function useCountdown() {
  const [timeLeft, setTimeLeft] = useState(() => {
    const saved = localStorage.getItem('offer-end-time-30m');
    if (saved) {
      const remaining = Math.max(0, Math.floor((parseInt(saved) - Date.now()) / 1000));
      if (remaining > 0) return remaining;
    }
    const endTime = Date.now() + 30 * 60 * 1000;
    localStorage.setItem('offer-end-time-30m', endTime.toString());
    return 30 * 60;
  });
  useEffect(() => {
    if (timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timeLeft]);
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor(timeLeft % 3600 / 60);
  const seconds = timeLeft % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return {
    hours,
    minutes,
    seconds,
    pad,
    timeLeft
  };
}
// Memoized digit component to prevent unnecessary re-renders
const TimeDigit = memo(({
  value
}: {
  value: string;
}) => <span className="bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded text-base md:text-lg font-extrabold" data-id="element-1">
    {value}
  </span>);
TimeDigit.displayName = 'TimeDigit';
export const CountdownBanner = memo(function CountdownBanner() {
  const {
    hours,
    minutes,
    seconds,
    pad
  } = useCountdown();
  return <div className="bg-gradient-to-r from-red-600 via-red-500 to-orange-500 text-white py-2.5 px-4 text-center relative z-50" data-id="element-2">
      <div className="max-w-4xl mx-auto flex items-center justify-center gap-3 text-sm md:text-base font-semibold" data-id="element-3">
        <ClockIcon className="w-4 h-4 md:w-5 md:h-5 animate-pulse flex-shrink-0" data-id="element-4" />
        <span data-id="element-5">🔥 OFERTA ESPECIAL — Termina en</span>
        <div className="flex items-center gap-1 font-mono" data-id="element-6">
          <TimeDigit value={pad(hours)} data-id="element-7" />
          <span className="font-bold" data-id="element-8">:</span>
          <TimeDigit value={pad(minutes)} data-id="element-9" />
          <span className="font-bold" data-id="element-10">:</span>
          <TimeDigit value={pad(seconds)} data-id="element-11" />
        </div>
      </div>
    </div>;
});
// Memoized box digit component
const BoxDigit = memo(({
  value,
  label
}: {
  value: string;
  label: string;
}) => <div className="flex flex-col items-center" data-id="element-12">
    <span className="bg-gradient-to-b from-amber-500 to-orange-600 text-white w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-extrabold shadow-lg shadow-amber-500/30" data-id="element-13">
      {value}
    </span>
    <span className="text-slate-500 text-[10px] uppercase tracking-wider mt-2" data-id="element-14">
      {label}
    </span>
  </div>);
BoxDigit.displayName = 'BoxDigit';
export const CountdownBox = memo(function CountdownBox() {
  const {
    hours,
    minutes,
    seconds,
    pad
  } = useCountdown();
  return <div className="flex items-center justify-center gap-3 mb-6" data-id="element-15">
      <BoxDigit value={pad(hours)} label="Horas" data-id="element-16" />
      <span className="text-amber-400 text-3xl font-bold -mt-5" data-id="element-17">:</span>
      <BoxDigit value={pad(minutes)} label="Min" data-id="element-18" />
      <span className="text-amber-400 text-3xl font-bold -mt-5" data-id="element-19">:</span>
      <BoxDigit value={pad(seconds)} label="Seg" data-id="element-20" />
    </div>;
});